// [PURPOSE]: Single row used in PostCreate share screen + PartyShareInviteModal:
//   label + monospace value + Copy button with "Copied!" feedback for 2s.
// [WHY]: Spec 2026-05-11-party-create-join — DRY between two share surfaces.
//   Inline copy buttons in different places lead to inconsistent UX + duplicated
//   "Copied!" timer state. One component, two consumers.
// [INTEGRATION]: Consumed by PartyCreateJoinSplash PostCreate sub-view and
//   PartyShareInviteModal. Depends on src/utils/copyToClipboard for the actual
//   clipboard write (with textarea fallback for older browsers).

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Copy, Check } from 'lucide-react';
import { copyToClipboard } from '../utils/copyToClipboard';

export function InviteShareCopyRow({ label, value }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    }
  }, [value]);

  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <div className="text-xs text-gray-400 mb-1">{label}</div>
        <div className="font-mono text-sm text-gray-100 truncate">{value}</div>
      </div>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : 'Copy'}
        className={`ml-3 px-3 py-2 rounded-md text-xs font-semibold flex items-center gap-1 ${
          copied
            ? 'bg-green-700 text-green-100'
            : 'bg-gray-700 hover:bg-gray-600 text-gray-100'
        }`}
      >
        {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy</>}
      </button>
    </div>
  );
}
